/**
 * Client read model. Snapshots are updated incrementally and synchronously from Yjs
 * events (so a new row can be focused inside the same key event — iOS keyboard), the
 * resolved tree is recomputed only on structural changes, and subscriptions are
 * fine-grained: typing in a row re-renders that row only.
 */
import * as Y from 'yjs'
import {
  computeMath,
  fieldsMap,
  overlayMath,
  todayIn,
  getYItem,
  itemsMap,
  readFields,
  readHouseholdSettings,
  readItem,
  readUsers,
  readUserSettings,
  resolveTree,
  settingsMap,
  userSettingsMap,
  usersMap,
  type FieldDef,
  type HouseholdSettings,
  type ItemSnapshot,
  type ItemMath,
  type ItemsIndex,
  type ResolvedTree,
  type UserInfo,
  type UserSettings,
} from '@terrazzo/core'
import { doc } from '../sync/doc'
import { deviceZone } from '../lib/clock'

const STRUCT_KEYS: Array<keyof ItemSnapshot> = [
  'parentId',
  'order',
  'dayOrder',
  'done',
  'date',
  'time',
  'trashedAt',
  'archivedAt',
  'pinned',
  'system',
  'recur',
  'completedAt',
  'assignee',
  'tags',
  'tint',
  'childFields',
  'labels',
  'completions',
  // Field values/saved-view definitions feed filters ("has field", saved views); they're
  // committed on blur, not per keystroke, so treating them as structural is cheap.
  'props',
  'page',
  'doneStyle',
  'rowFields',
  'pageOrder',
  'childPageOrder',
  'icon',
  // Math (SPEC §7.12): what equations read and where they're set.
  'calcInside',
  'calcLines',
  'total',
]

function same(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false
    for (let i = 0; i < a.length; i++) if (!same(a[i], b[i])) return false
    return true
  }
  if (a && b && typeof a === 'object' && typeof b === 'object') return JSON.stringify(a) === JSON.stringify(b)
  return false
}

type Fn = () => void

export class Store {
  /** Items as read from the doc. */
  raw = new Map<string, ItemSnapshot>()
  /** Items with their math worked in (what everything reads; answers are never written back). */
  snaps = new Map<string, ItemSnapshot>()
  /** Every item's math, worked out when anything it could read changes. */
  math = new Map<string, ItemMath>()
  tree: ResolvedTree = resolveTree([])
  structureVersion = 0
  contentVersion = 0
  metaVersion = 0
  private index: ItemsIndex = { items: this.snaps, tree: this.tree }
  private itemSubs = new Map<string, Set<Fn>>()
  private structSubs = new Set<Fn>()
  private contentSubs = new Set<Fn>()
  private metaSubs = new Set<Fn>()
  private metaCache: {
    household?: HouseholdSettings
    users?: Map<string, UserInfo>
    fields?: Map<string, FieldDef>
    userSettings: Map<string, UserSettings & Record<string, unknown>>
  } = { userSettings: new Map() }

  constructor(private ydoc: Y.Doc) {
    this.rebuild()
    itemsMap(ydoc).observeDeep((events) => this.onItemEvents(events))
    const meta = () => {
      this.metaCache = { userSettings: new Map() }
      this.metaVersion++
      for (const f of this.metaSubs) f()
    }
    settingsMap(ydoc).observeDeep(meta)
    userSettingsMap(ydoc).observeDeep(meta)
    usersMap(ydoc).observeDeep(meta)
    fieldsMap(ydoc).observeDeep(meta)
    // A detail's name or kind changes how its answers read.
    fieldsMap(ydoc).observeDeep(() => {
      const changed = this.rework()
      if (!changed.size) return
      this.contentVersion++
      for (const id of changed) for (const f of [...(this.itemSubs.get(id) ?? [])]) f()
      for (const f of [...this.contentSubs]) f()
    })
  }

  /**
   * Work out the math again and put it into the items: returns the ids whose answers changed
   * (their snapshots are new, so whatever shows them updates).
   */
  private rework(): Set<string> {
    const before = this.math
    const fields = this.fields()
    this.math = computeMath({ items: this.raw, tree: this.tree }, fields, todayIn(deviceZone()))
    const changed = new Set<string>()
    for (const id of new Set([...before.keys(), ...this.math.keys()])) {
      const raw = this.raw.get(id)
      if (!raw) continue
      if (JSON.stringify(before.get(id)) === JSON.stringify(this.math.get(id)) && this.snaps.get(id) && this.snaps.get(id) !== raw) continue
      this.snaps.set(id, overlayMath(raw, this.math.get(id), fields))
      changed.add(id)
    }
    return changed
  }

  rebuild() {
    this.raw = new Map()
    itemsMap(this.ydoc).forEach((y, id) => {
      if (y instanceof Y.Map) this.raw.set(id, readItem(id, y))
    })
    this.tree = resolveTree(this.raw)
    this.math = computeMath({ items: this.raw, tree: this.tree }, this.fields(), todayIn(deviceZone()))
    this.snaps = new Map()
    for (const [id, it] of this.raw) this.snaps.set(id, overlayMath(it, this.math.get(id), this.fields()))
    this.index = { items: this.snaps, tree: this.tree }
    this.structureVersion++
    this.contentVersion++
    for (const f of this.structSubs) f()
    for (const f of this.contentSubs) f()
    for (const set of this.itemSubs.values()) for (const f of set) f()
  }

  private onItemEvents(events: Array<Y.YEvent<Y.AbstractType<unknown>>>) {
    const items = itemsMap(this.ydoc)
    const dirty = new Set<string>()
    for (const e of events) {
      if (e.target === items) for (const k of (e as Y.YMapEvent<unknown>).keysChanged) dirty.add(k)
      else if (typeof e.path[0] === 'string') dirty.add(e.path[0])
    }
    if (!dirty.size) return
    let structural = false
    for (const id of dirty) {
      const prev = this.raw.get(id)
      const y = getYItem(this.ydoc, id)
      if (!y) {
        if (prev) {
          this.raw.delete(id)
          this.snaps.delete(id)
          structural = true
        }
        continue
      }
      const next = readItem(id, y)
      this.raw.set(id, next)
      this.snaps.set(id, overlayMath(next, this.math.get(id), this.fields()))
      if (!prev || STRUCT_KEYS.some((k) => !same(prev[k], next[k]))) structural = true
      // Cycle breaking picks the most recently edited item, so while a cycle exists any
      // edit can change the resolved tree.
      else if (this.tree.cycleBreaks.size && prev.updatedAt !== next.updatedAt) structural = true
    }
    if (structural) {
      this.tree = resolveTree(this.raw)
      this.structureVersion++
      // Anything an equation reads may have changed: answers above it too.
      for (const id of this.rework()) dirty.add(id)
    }
    this.index = { items: this.snaps, tree: this.tree }
    this.contentVersion++
    if (structural) for (const f of [...this.structSubs]) f()
    for (const id of dirty) {
      const subs = this.itemSubs.get(id)
      if (subs) for (const f of [...subs]) f()
    }
    for (const f of [...this.contentSubs]) f()
  }

  get idx(): ItemsIndex {
    return this.index
  }

  get(id: string): ItemSnapshot | undefined {
    return this.snaps.get(id)
  }

  subscribeItem(id: string, fn: Fn): () => void {
    let set = this.itemSubs.get(id)
    if (!set) this.itemSubs.set(id, (set = new Set()))
    set.add(fn)
    return () => {
      set!.delete(fn)
      if (!set!.size) this.itemSubs.delete(id)
    }
  }
  subscribeStructure = (fn: Fn) => {
    this.structSubs.add(fn)
    return () => this.structSubs.delete(fn)
  }
  subscribeContent = (fn: Fn) => {
    this.contentSubs.add(fn)
    return () => this.contentSubs.delete(fn)
  }
  subscribeMeta = (fn: Fn) => {
    this.metaSubs.add(fn)
    return () => this.metaSubs.delete(fn)
  }

  household(): HouseholdSettings {
    return (this.metaCache.household ??= readHouseholdSettings(this.ydoc))
  }
  users(): Map<string, UserInfo> {
    return (this.metaCache.users ??= readUsers(this.ydoc))
  }
  fields(): Map<string, FieldDef> {
    return (this.metaCache.fields ??= readFields(this.ydoc))
  }
  userSettings(userId: string): UserSettings & Record<string, unknown> {
    let s = this.metaCache.userSettings.get(userId)
    if (!s) {
      s = readUserSettings(this.ydoc, userId)
      this.metaCache.userSettings.set(userId, s)
    }
    return s
  }
}

export const store = new Store(doc)
